import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { number, shape, string, bool, func } from 'prop-types'
import { css } from '@emotion/core'
import { useAtSize, Avatar } from '@fs/zion-ui'
import { determineAncestorReferenceLabel } from './helpers/ancestor_Determinant'
import { determineGender } from '../shared/helpers/gedcom_X'
import { useTreeDispatch } from './state/TreeStore'

export default function AncestorCard({ disabled, personData, setIsTreeShowing }) {
  const atSize = useAtSize()
  const [t] = useTranslation()
  const [imageError, setImageError] = useState(false)
  const treeDispatch = useTreeDispatch()

  const cardViewCss = css`
    ${determineGender(personData.genderType) === 'male' && 'border-top: 6px solid #01b6d1'};
    ${determineGender(personData.genderType) === 'female' && 'border-top: 6px solid #bd0057'};
    ${determineGender(personData.genderType) === 'unknown' && 'border-top: 6px solid #aaa'};
    ${atSize({ default: 'width: calc(100vw / 5.25)', md: 'width: 120px' })};
    ${atSize({ default: 'height: calc(100vw / 3)', md: 'height: 175px' })};
    a,
    a:visited {
      text-decoration: none;
      color: #177c9c;
    }
  `
  const squareCardCss = css`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: #fff;
    border: none;
    outline: none;
    &:hover {
      background-color: #eee;
      ${disabled ? 'cursor: not-allowed' : 'cursor: pointer'};
    }
  `

  const avatarImgCss = css`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    ${atSize({
      default: `
        width: 40px;
        height: 40px;
      `,
      lg: `
        width: 88px;
        height: 88px;
      `,
    })};
  `

  const lifeSpanCss = css`
    color: gray;
    ${atSize({ default: 'display: none;', md: 'font-size: 10px;' })};
  `

  const nameCss = css`
    font-weight: bold;
    text-overflow: ellipsis;
    overflow: hidden;
    ${atSize({
      default: 'font-size: 10px;',
      md: 'font-size: 12px; padding-bottom: 4px; padding-top: 4px;',
      lg: 'font-size: 13px; padding-bottom: 8px; padding-top: 4px;',
    })};
  `

  const ancestorItem = css`
    flex: 1;
    ${atSize({ default: 'margin-top: none', md: 'margin-top: 10px' })};
  `
  const ancestorImg = css`
    display: flex;
    align-items: center;
  `

  const determineTokenizedIdentifier = () => {
    const tokenSet = determineAncestorReferenceLabel(personData.atn, personData.genderType)
    return t(tokenSet.key, tokenSet.defaultValue)
  }

  const buildName = () => {
    if (personData.displayName) {
      return personData.displayName
    }

    return determineTokenizedIdentifier()
  }

  const handleClick = () => {
    treeDispatch({
      type: 'EDIT_ANCESTOR',
      payload: {
        atn: personData.atn,
      },
    })
    setIsTreeShowing(false)
  }

  return (
    personData && (
      <div css={cardViewCss}>
        <button css={squareCardCss} type="button" disabled={disabled} onClick={handleClick}>
          <div css={[ancestorItem, ancestorImg]}>
            {personData?.portraitThumbnailLink && !imageError ? (
              <img
                css={avatarImgCss}
                alt="placeholder"
                src={personData.portraitThumbnailLink}
                onError={() => setImageError(true)}
              />
            ) : (
              <Avatar size={atSize({ default: 'md', lg: 'lg' })} sex={determineGender(personData?.genderType)} />
            )}
          </div>
          <div css={ancestorItem}>
            <div css={nameCss}>
              <span>
                {personData.name && personData.surname ? `${personData.name} ${personData.surname}` : buildName()}
              </span>
            </div>
            <div css={lifeSpanCss}>{personData.lifespan || ''}</div>
          </div>
        </button>
      </div>
    )
  )
}

AncestorCard.propTypes = {
  disabled: bool,
  personData: shape({
    name: string,
    atn: number,
    surname: string,
    displayName: string,
    genderType: string,
    portraitThumbnailLink: string,
    lifespan: string,
  }),
  setIsTreeShowing: func,
}
